/**
 * P2-WORKSPACE — Design Session orchestrator (the vertical slice, headless).
 *
 * The full designer loop in one typed, deterministic module:
 *   retrieve (D3 via D7) → adjust (overrides w/ provenance) → render
 *   (RenderFacet from the SAME state) → version (append-only log) →
 *   approve (human act).
 *
 * Binding rules:
 * - The renderer's input state and the version log's state are THE SAME
 *   OBJECT — render/number consistency is structural, not aspirational.
 * - Recalculation is immediate: every mutation returns a fresh derived view
 *   (validity, curve, facet) synchronously.
 * - Approval requires: no unresolved bounded-adjustment flags, geometric +
 *   production validity, and an actor recorded.
 * - Pure + deterministic; actors/timestamps caller-provided.
 */
import {
  applyOverride,
  approveCurrent,
  type DesignState,
  type DesignVersionLog,
  type OverrideRecord,
  startDesignVersion,
} from './designVersioning';
import { recommend, type RecommendationEngineOutput } from './recommendation';
import { buildRenderFacet, type RenderFacet } from './renderFacet';
import type { ReferenceLibrary } from './referenceLibrary';
import type { SkiType } from './schemas';

export const DESIGN_SESSION_VERSION = '0.1';

export interface DesignSessionInput {
  /** Engine profile — SkiType union enforced at the boundary (RtaProfile). */
  profile: { skiType: SkiType; lengthCm: number };
  /** Interview payload (anchors draft from it) — optional in v0.1 slice. */
  interview?: Record<string, unknown>;
  lib: ReferenceLibrary;
}

export interface DesignSessionView {
  sessionVersion: string;
  engine: RecommendationEngineOutput;
  state: DesignState;
  versions: DesignVersionLog;
  facet: RenderFacet | null;
  /** Approval blockers — empty when the session CAN be approved. */
  blockers: string[];
}

function stateFromRecommendation(engine: RecommendationEngineOutput): DesignState {
  const rec = engine.recommendation;
  return {
    skiType: rec.skiType.value,
    length: rec.length.value,
    noseWidth: rec.noseWidth.value,
    tailWidth: rec.tailWidth.value,
    waistWidthRecommendation: rec.waistWidthRecommendation.value,
    flexPattern: rec.flexPattern?.value ?? 'Balanced',
    noseBlockId: engine.recommendation.construction?.value?.noseBlockId,
    tailBlockId: engine.recommendation.construction?.value?.tailBlockId,
  };
}

/** Sample the plan-view perimeter polyline (mm, facet frame) for widest
 *  measurement — a compact local port of the PDF's sampler for the two
 *  end-zone bins (does not need the full fidelity of the renderer path). */
function samplePlanPolyForWidest(curve: NonNullable<RecommendationEngineOutput['profileCurve']>): Array<[number, number]> {
  const arcs = (curve as unknown as { arcs1?: Array<{ x: number; y: number; r: number; s: number; e: number; d: 1 | -1 }> }).arcs1 ?? [];
  const pts: Array<[number, number]> = [];
  const N = 24;
  for (const a of arcs) {
    let s0 = a.s, e0 = a.e;
    if (a.d === -1) { const t = s0; s0 = e0; e0 = t; }
    const sweep = e0 - s0;
    for (let k = 0; k <= N; k++) {
      const t = s0 + (sweep * k) / N;
      pts.push([a.x + a.r * Math.cos(t), a.y + a.r * Math.sin(t)]);
    }
  }
  return pts;
}

/** End->widest-point station, mm, measured from the sampled plan-view
 *  outline (argmax width in the end 30% of the span). Shared derivation with
 *  the PDF renderer's S2/S3 so the sideview grounds and bracket ticks use
 *  ONE formula (Pete 2026-10-26 camber-limits ruling). */
function widestFromEndMm(poly: Array<[number, number]>, end: 'nose' | 'tail'): number | undefined {
  if (!poly.length) return undefined;
  let minX = Infinity, maxX = -Infinity;
  for (const [x] of poly) { if (x < minX) minX = x; if (x > maxX) maxX = x; }
  const span = maxX - minX;
  if (!(span > 0)) return undefined;
  const nBins = 366;
  const bins = new Array(nBins).fill(0).map(() => ({ lo: Infinity, hi: -Infinity }));
  for (const [x, y] of poly) {
    const b = Math.min(nBins - 1, Math.max(0, Math.floor(((x - minX) / span) * nBins)));
    if (y < bins[b].lo) bins[b].lo = y;
    if (y > bins[b].hi) bins[b].hi = y;
  }
  const zone = end === 'nose'
    ? bins.slice(0, Math.floor(nBins * 0.3))
    : bins.slice(Math.ceil(nBins * 0.7));
  let best = -1;
  for (let i = 0; i < zone.length; i++) {
    const w = zone[i].hi - zone[i].lo;
    if (w > best) best = w;
    if (w === best && best >= 0) { /* keep first */ }
  }
  if (best < 0) return undefined;
  const bi = end === 'nose'
    ? zone.reduce((acc, z, i) => (z.hi - z.lo) === best && acc === -1 ? i : acc, -1)
    : zone.reduce((acc, z, i) => (z.hi - z.lo) === best ? i : acc, -1);
  if (bi < 0) return undefined;
  // bin index -> mm from the END (facet key frame: x from tail end)
  const binCenterX = minX + ((end === 'nose' ? bi + Math.floor(nBins * 0.7) * 0 : 0) + bi + 0.5) * (span / nBins);
  // careful: zone slice starts at 0 for nose, at 0.7*nBins for tail
  const binIdxAbs = end === 'nose' ? bi : Math.ceil(nBins * 0.7) + bi;
  const xAbs = minX + (binIdxAbs + 0.5) * (span / nBins);
  return end === 'nose' ? span - xAbs + minX * 0 : xAbs - minX;
}

function facetFromState(engine: RecommendationEngineOutput, state: DesignState): RenderFacet | null {
  // v0.1 slice: facet renders from the engine's own arc path when present
  // (the engine's profileCurve carries the authoritative renderer input).
  const curve = engine.profileCurve;
  if (!curve) return null;
  const arcs1Pts = samplePlanPolyForWidest(curve);
  return buildRenderFacet({
    arcs1: (curve as unknown as { arcs1?: RenderFacet['arcs1'] }).arcs1 ?? [],
    key: {
      tailX: 0,
      waistX: Math.round((state.length * 10) / 2),
      noseX: state.length * 10,
      tailWidthMm: state.tailWidth,
      waistWidthMm: state.waistWidthRecommendation,
      noseWidthMm: state.noseWidth,
    },
    profile: {
      noseHeightMm: curve.nose.arc.heightMm,
      noseLengthToContactMm: curve.nose.arc.lengthToContactMm,
      noseRadiusMm: curve.nose.arc.upturnRadiusMm,
      // End->widest stations from the plan-view arcs (same measurement the
      // PDF's S2/S3 uses: argmax width in the end 30% of the sampled
      // outline). Grounds the sideview's y=0 camber limits at the
      // rocker-start ticks (Pete 2026-10-26: "Y = 0 at the camber limits").
      noseWidestFromEndMm: widestFromEndMm(arcs1Pts, 'nose'),
      tailWidestFromEndMm: widestFromEndMm(arcs1Pts, 'tail'),
      tailHeightMm: curve.tail.arc.heightMm,
      tailLengthToContactMm: curve.tail.arc.lengthToContactMm,
      tailRadiusMm: curve.tail.arc.upturnRadiusMm,
      runningLengthMm: curve.runningLengthMm,
      camberEnum: curve.camber.value,
      camberHeightMm: curve.camberHeightMm,
      tipRockerName: curve.nose.rocker?.name ?? '',
      tailRockerName: curve.tail.rocker?.name ?? '',
      lengthMm: curve.lengthMm,
      noseWidthMm: state.noseWidth,
      waistWidthMm: state.waistWidthRecommendation,
      tailWidthMm: state.tailWidth,
      sidecutRadiusM: 0, // sidecut lives in the arcs; not re-derived here
    },
  });
}

function blockersFor(engine: RecommendationEngineOutput, versions: DesignVersionLog): string[] {
  const blockers: string[] = [];
  for (const u of engine.recommendation.unresolved ?? []) {
    blockers.push(`unresolved: ${u}`);
  }
  if (engine.recommendation.validity.geometric !== 'valid') {
    blockers.push(`geometric validity: ${engine.recommendation.validity.geometric}`);
  }
  if (engine.recommendation.validity.production !== 'valid') {
    blockers.push(`production validity: ${engine.recommendation.validity.production}`);
  }
  if (!versions.current) blockers.push('no design version exists');
  return blockers;
}

/**
 * Start a session: run the engine, snapshot version 1 from its output.
 */
export function startDesignSession(input: DesignSessionInput): DesignSessionView {
  const engine = recommend({
    profile: input.profile,
    interview: input.interview as never,
    lib: input.lib,
  });
  const state = stateFromRecommendation(engine);
  const versions = startDesignVersion(state, engine.precedent
    ? {
        serial: engine.precedent.record.serial ?? undefined,
        orderFormId: engine.precedent.record.orderFormId,
      }
    : undefined);
  return {
    sessionVersion: DESIGN_SESSION_VERSION,
    engine,
    state,
    versions,
    facet: facetFromState(engine, state),
    blockers: blockersFor(engine, versions),
  };
}

/** Designer adjusts ONE field → immediate recalculation → new draft version. */
export function adjustDesign(
  session: DesignSessionView,
  override: OverrideRecord,
  lib: ReferenceLibrary,
): DesignSessionView {
  const versions = applyOverride(session.versions, override);
  // Re-run the engine against the ADJUSTED ask so validity/curve/facet all
  // reflect the current state (immediate recalculation rule).
  const skiType = versions.current!.state.skiType as SkiType;
  const engine = recommend({
    profile: { skiType, lengthCm: versions.current!.state.length },
    lib,
  });
  // The designer's override IS the truth: engine re-derives shape around it,
  // then every recorded override is re-applied on top (immutably, in order).
  const mergedState: DesignState = {
    ...stateFromRecommendation(engine),
    ...pickOverrides(versions),
  } as DesignState;
  return {
    sessionVersion: session.sessionVersion,
    engine,
    state: mergedState,
    versions,
    facet: facetFromState(engine, mergedState),
    blockers: blockersFor(engine, versions),
  };
}

/** Collect every override's latest value per field (the designer's truth). */
function pickOverrides(versions: DesignVersionLog): Partial<DesignState> {
  const out: Partial<DesignState> = {};
  for (const v of versions.versions) {
    for (const o of v.overrides) {
      (out as Record<string, unknown>)[o.field] = o.newValue;
    }
  }
  return out;
}

/** Designer approves — only when no blockers remain. */
export function approveSession(
  session: DesignSessionView,
  by: string,
  at: string,
): DesignSessionView {
  if (session.blockers.length > 0) {
    throw new Error(`cannot approve: ${session.blockers.join('; ')}`);
  }
  const versions = approveCurrent(session.versions, by, at);
  return { ...session, versions };
}